import { DisplayLocale } from "../../../../lib/Money.ts";
import { formatQuinnEuroFromCents } from "../../utils/formatters.ts";
import { QuinnDocumentLink } from "../Sources/QuinnDocumentLink.tsx";

export type QuinnTableColumnKind = "text" | "money" | "number" | "percent";

export type QuinnTableColumn = {
    label: string;
    kind: QuinnTableColumnKind;
};

export type QuinnTableValue = string | number | null;

export type QuinnLinkedTableCell = {
    value: QuinnTableValue;
    document_id: string;
    client: string;
};

export type QuinnTableCell = QuinnTableValue | QuinnLinkedTableCell;

export type QuinnTableSpec = {
    id?: string;
    title: string;
    subtitle?: string | null;
    columns: QuinnTableColumn[];
    rows: QuinnTableCell[][];
    totals?: QuinnTableCell[] | null;
};

const COLUMN_KINDS: Record<QuinnTableColumnKind, true> = { text: true, money: true, number: true, percent: true };

function isColumnKind(kind: unknown): kind is QuinnTableColumnKind {
    return typeof kind === "string" && Object.prototype.hasOwnProperty.call(COLUMN_KINDS, kind);
}

const numberFormat = new Intl.NumberFormat(DisplayLocale.NL_NL, { maximumFractionDigits: 2 });

function isTableValue(value: unknown): value is QuinnTableValue {
    return value === null || typeof value === "string" || typeof value === "number";
}

function isLinkedCell(cell: unknown): cell is QuinnLinkedTableCell {
    if (!cell || typeof cell !== "object") return false;
    const linked = cell as Partial<QuinnLinkedTableCell>;
    return typeof linked.document_id === "string" && typeof linked.client === "string" && isTableValue(linked.value);
}

export function isQuinnTableSpec(value: unknown): value is QuinnTableSpec {
    if (!value || typeof value !== "object") return false;
    const spec = value as Partial<QuinnTableSpec>;
    if (typeof spec.title !== "string" || !Array.isArray(spec.columns) || !Array.isArray(spec.rows)) return false;
    if (spec.columns.length === 0 || spec.rows.length === 0) return false;
    const width = spec.columns.length;
    const columnsAreValid = spec.columns.every(
        (column) => typeof column?.label === "string" && isColumnKind(column?.kind),
    );
    const isCell = (cell: unknown) => isTableValue(cell) || isLinkedCell(cell);
    const rowFits = (row: unknown) => Array.isArray(row) && row.length === width && row.every(isCell);
    return columnsAreValid && spec.rows.every(rowFits) && (spec.totals == null || rowFits(spec.totals));
}

export function formatQuinnTableCell(cell: QuinnTableCell, kind: QuinnTableColumnKind): string {
    const value = isLinkedCell(cell) ? cell.value : cell;
    if (value === null || value === "") return "";
    if (typeof value === "string") return value;
    switch (kind) {
        case "money":
            return formatQuinnEuroFromCents(value);
        case "percent":
            return `${numberFormat.format(value)}%`;
        case "number":
        case "text":
            return numberFormat.format(value);
    }
}

function QuinnTableCellContent({ cell, kind }: { cell: QuinnTableCell; kind: QuinnTableColumnKind }) {
    const text = formatQuinnTableCell(cell, kind);
    if (!text || !isLinkedCell(cell)) return <>{text}</>;
    return (
        <QuinnDocumentLink target={{ documentId: cell.document_id, businessId: cell.client }}>{text}</QuinnDocumentLink>
    );
}

type QuinnTableBlockProps = {
    spec: QuinnTableSpec;
    className?: string;
    showTitle?: boolean;
};

export function QuinnTableBlock({ spec, className = "max-h-96", showTitle = true }: QuinnTableBlockProps) {
    const alignment = (kind: QuinnTableColumnKind) => (kind === "text" ? "text-left" : "text-right");

    return (
        <figure className="my-3">
            {showTitle && (
                <figcaption className="mb-1.5">
                    <span className="block text-[11px] font-medium uppercase tracking-wider text-gray-500">{spec.title}</span>
                    {spec.subtitle && <span className="block text-[11px] text-gray-400">{spec.subtitle}</span>}
                </figcaption>
            )}
            <div className={`overflow-auto rounded-xl border border-gray-200 bg-white ${className}`}>
                <table className="w-full border-separate border-spacing-0 text-xs">
                    <thead className="sticky top-0 z-10 bg-gray-50">
                        <tr>
                            {spec.columns.map((column, index) => (
                                <th
                                    key={index}
                                    scope="col"
                                    className={`whitespace-nowrap border-b border-gray-200 px-2.5 py-2 text-[10px] font-medium uppercase tracking-wider text-gray-500 ${alignment(column.kind)}`}
                                >
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {spec.rows.map((row, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-gray-50/60">
                                {row.map((cell, cellIndex) => (
                                    <td
                                        key={cellIndex}
                                        className={`border-b border-gray-100 px-2.5 py-1.5 tabular-nums text-gray-800 ${alignment(spec.columns[cellIndex].kind)}`}
                                    >
                                        <QuinnTableCellContent cell={cell} kind={spec.columns[cellIndex].kind} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                    {spec.totals && (
                        <tfoot className="sticky bottom-0 bg-gray-50">
                            <tr>
                                {spec.totals.map((cell, cellIndex) => (
                                    <td
                                        key={cellIndex}
                                        className={`border-t-2 border-gray-300 px-2.5 py-2 font-semibold tabular-nums text-gray-900 ${alignment(spec.columns[cellIndex].kind)}`}
                                    >
                                        <QuinnTableCellContent cell={cell} kind={spec.columns[cellIndex].kind} />
                                    </td>
                                ))}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </figure>
    );
}
